import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import Loading from '../../../../../components/loading';
import { useProceduresCatalog } from '../../../../../hooks/useProceduresCatalog';
import { useProfessionals } from '../../../../../hooks/useProfessionals';
import { useProcedureRectifications } from '../hooks/useProcedureRectifications';

const currency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

const EMPTY = '—';

function toothLabel(data) {
  if (!data.tooth) return 'Geral';
  if (data.tooth === 'TODOS_DECIDUOS') return 'Todos os decíduos';
  if (data.tooth === 'TODOS_PERMANENTES') return 'Todos os permanentes';
  return data.toothDescription ? `D${data.toothDescription}` : (data.tooth ?? EMPTY);
}

// Só as linhas que mudaram entre o antes e o depois de cada retificação.
function changesOf(rectification, catalogName, professionalName) {
  const { previous, current } = rectification;
  const procedureName = (data) =>
    data.procedureCatalogId ? catalogName(data.procedureCatalogId) : (data.description ?? EMPTY);

  const fields = [
    { label: 'Procedimento', from: procedureName(previous), to: procedureName(current) },
    { label: 'Dente', from: toothLabel(previous), to: toothLabel(current) },
    {
      label: 'Profissional',
      from: professionalName(previous.professionalId),
      to: professionalName(current.professionalId),
    },
    {
      label: 'Valor',
      from: previous.amount != null ? currency.format(previous.amount) : EMPTY,
      to: current.amount != null ? currency.format(current.amount) : EMPTY,
    },
    {
      label: 'Observação',
      from: previous.observation || EMPTY,
      to: current.observation || EMPTY,
    },
  ];
  return fields.filter((field) => field.from !== field.to);
}

export default function RectificationHistoryModal({ open, onClose, procedure }) {
  const { rectifications, isLoading } = useProcedureRectifications(open ? procedure?.id : null);
  const { proceduresCatalog: catalog } = useProceduresCatalog();
  const { professionals } = useProfessionals();

  const catalogName = (id) => catalog.find((item) => item.id === id)?.description ?? `#${id}`;
  const professionalName = (id) =>
    id == null
      ? 'Profissional do tratamento'
      : (professionals.find((p) => String(p.id) === String(id))?.nome ?? `#${id}`);

  return (
    <Dialog
      data-cy="rectification-history-modal"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: '1000px', maxWidth: '900px', minHeight: '400px' } } }}
    >
      <Loading isToHidden={isLoading === false} />
      <DialogTitle>
        <Typography sx={{ fontWeight: 'bold' }}>Retificações do Procedimento</Typography>
        <Typography variant="body2" color="text.secondary">
          {procedure?.descricao}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {rectifications.length > 0 ? (
          <Timeline sx={{ mt: 1, [`& .MuiTimelineItem-root::before`]: { flex: 0, padding: 0 } }}>
            {rectifications.map((rectification, index) => (
              <TimelineItem key={rectification.id}>
                <TimelineSeparator>
                  <TimelineDot color="warning" variant="outlined" />
                  {index < rectifications.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent sx={{ py: '12px', px: 2 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {dayjs(rectification.rectifiedAt).format('DD/MM/YYYY HH:mm')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    por {rectification.rectifiedBy}
                  </Typography>
                  <Stack spacing={0.5} sx={{ mt: 1 }}>
                    {changesOf(rectification, catalogName, professionalName).map((change) => (
                      <Typography key={change.label} variant="body2" data-cy="rectification-change">
                        <strong>{change.label}:</strong>{' '}
                        <Box
                          component="span"
                          sx={{ color: 'text.secondary', textDecoration: 'line-through' }}
                        >
                          {change.from}
                        </Box>{' '}
                        → {change.to}
                      </Typography>
                    ))}
                  </Stack>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        ) : (
          !isLoading && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                Nenhuma retificação registrada para este procedimento.
              </Typography>
            </Box>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}

RectificationHistoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  procedure: PropTypes.shape({
    id: PropTypes.number.isRequired,
    descricao: PropTypes.string,
  }),
};
